<template>
  <b-modal 
    id="modal-schedule-employees" 
    centered 
    hide-footer 
    title="Attendance"
    size="xl"
    @hidden="onModalClose">
    <div class="modal-body-custom">
      <!-- Loading State -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p class="mt-3 text-muted">Loading attendance details...</p>
      </div>

      <!-- Content -->
      <div v-else>
        <!-- Download Button -->
        <div class="mb-4 d-flex justify-content-between align-items-center">
          <h5 class="mb-0">Download Attendance</h5>
          <button class="btn btn-danger btn-sm" @click="downloadAttendance">
            <i class="fe fe-download me-1"></i>
            Attendance
          </button>
        </div>

        <!-- Training Details -->
        <div class="row mb-4">
          <div class="col-md-4 mb-3">
            <div class="detail-item">
              <label class="detail-label">Title</label>
              <div class="detail-value">{{ trainingData.training_title || '-' }}</div>
            </div>
          </div>
          <div class="col-md-4 mb-3">
            <div class="detail-item">
              <label class="detail-label">Batch</label>
              <div class="detail-value">{{ formatBatchLabel(trainingData) }}</div>
            </div>
          </div>
          <div class="col-md-4 mb-3">
            <div class="detail-item">
              <label class="detail-label">Date</label>
              <div class="detail-value">{{ formatDate(trainingData.schedule_date) }}</div>
            </div>
          </div>
          <div class="col-md-4 mb-3">
            <div class="detail-item">
              <label class="detail-label">Total Participant</label>
              <div class="detail-value">{{ trainingData.total_participants || 0 }}</div>
            </div>
          </div>
          <div class="col-md-4 mb-3">
            <div class="detail-item">
              <label class="detail-label">Present</label>
              <div class="detail-value">{{ trainingData.present_count || 0 }}</div>
            </div>
          </div>
          <div class="col-md-4 mb-3">
            <div class="detail-item">
              <label class="detail-label">Absent</label>
              <div class="detail-value">{{ trainingData.absent_count || 0 }}</div>
            </div>
          </div>
        </div>

        <!-- Attendance List -->
        <h6 class="mb-3">Attendance List</h6>
        <div class="table-responsive">
          <table class="table table-hover table-nowrap attendance-table">
            <thead>
              <tr>
                <th>NO</th>
                <th>NIK</th>
                <th>EMPLOYEE NAME</th>
                <th>COMPANY</th>
                <th>DIVISION</th>
                <th>POSITION</th>
                <th>STATUS</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!employees || employees.length === 0">
                <td colspan="7" class="text-center py-4">
                  <div class="text-muted">
                    <p class="mb-0">No attendance data found</p>
                  </div>
                </td>
              </tr>
              <tr v-for="(employee, index) in employees" :key="employee.participant_id">
                <td>{{ getRowNumber(index) }}</td>
                <td>{{ employee.nik || '-' }}</td>
                <td>{{ employee.fullname || '-' }}</td>
                <td>{{ employee.company || '-' }}</td>
                <td>{{ employee.divisi || '-' }}</td>
                <td>{{ employee.participant_position || '-' }}</td>
                <td>
                  <span 
                    class="badge"
                    :class="employee.attendance_status === 'Present' ? 'badge-success' : 'badge-danger'"
                  >
                    {{ employee.attendance_status }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="d-flex justify-content-between align-items-center mt-4">
          <div class="d-flex align-items-center">
            <div class="text-muted small me-3">
              Showing {{ getStartRow() }} to {{ getEndRow() }} of {{ pagination.total }} entries
            </div>
            <div class="d-flex align-items-center">
              <label class="text-muted small me-2 mb-0">Show</label>
              <select 
                v-model.number="pagination.limit" 
                @change="handlePageSizeChange"
                class="form-select form-select-sm page-size-select"
              >
                <option v-for="size in pageSizeOptions" :key="size" :value="size">
                  {{ size }}
                </option>
              </select>
              <label class="text-muted small ms-2 mb-0">entries</label>
            </div>
          </div>
          <b-pagination
            v-model="currentPage"
            :total-rows="pagination.total"
            :per-page="pagination.limit"
            @change="handlePageChange"
            class="mb-0"
          />
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import toast from '@/utils/toast';
import { formatBatchLabel } from '@/utils/batchIdentity';

export default {
  name: 'ModalScheduleEmployees',
  props: {
    scheduleId: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      loading: false,
      currentPage: 1,
      pageSizeOptions: [10, 25, 50, 100],
      trainingData: {
        training_title: '',
        batch_number: 0,
        schedule_date: '',
        total_participants: 0,
        present_count: 0,
        absent_count: 0
      },
      employees: [],
      pagination: {
        total: 0,
        page: 1,
        limit: 10,
        totalPage: 0
      }
    };
  },
  watch: {
    scheduleId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.currentPage = 1;
          this.fetchEmployees();
        }
      }
    }
  },
  methods: {
    formatBatchLabel,
    async fetchEmployees() {
      if (!this.scheduleId) return;

      this.loading = true;
      try {
        const params = {
          page: this.currentPage,
          limit: this.pagination.limit
        };

        const response = await this.$store.dispatch('attendanceReport/fetchScheduleEmployees', {
          id: this.scheduleId,
          params
        });
        
        if (response && response.data) {
          this.trainingData = response.data.trainingData || {
            training_title: '',
            batch_number: 0,
            schedule_date: '',
            total_participants: 0,
            present_count: 0,
            absent_count: 0
          };

          this.employees = response.data.data || [];
          
          this.pagination = {
            total: response.data.total || 0,
            page: response.data.page || 1,
            limit: response.data.limit || 10,
            totalPage: response.data.totalPage || 0
          };
        }
      } catch (error) {
        console.error('Error fetching schedule employees:', error);
        toast({ type: 'error', title: 'Error', message: 'Data absensi belum berhasil dimuat' });
      } finally {
        this.loading = false;
      }
    },

    handlePageChange(page) {
      this.currentPage = page;
      this.fetchEmployees();
    },

    handlePageSizeChange() {
      this.currentPage = 1;
      this.fetchEmployees();
    },

    getRowNumber(index) {
      return (this.pagination.page - 1) * this.pagination.limit + index + 1;
    },

    getStartRow() {
      return (this.pagination.page - 1) * this.pagination.limit + 1;
    },

    getEndRow() {
      const end = this.pagination.page * this.pagination.limit;
      return end > this.pagination.total ? this.pagination.total : end;
    },
    
    formatDate(date) {
      if (!date) return '-';
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}-${month}-${year}`;
    },
    
    async downloadAttendance() {
      if (!this.scheduleId) {
        toast({ type: 'error', title: 'Error', message: 'Schedule ID is missing' });
        return;
      }

      try {
        const response = await this.$store.dispatch('attendanceReport/downloadScheduleReport', this.scheduleId);
        
        // Create a blob from the response
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const fileName = `attendance-${this.trainingData.training_title || 'report'}-${this.formatDate(this.trainingData.schedule_date)}.xlsx`;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast({ type: 'success', title: 'Success', message: 'Absensi berhasil diunduh' });
      } catch (error) {
        console.error('Error downloading attendance:', error);
        toast({ type: 'error', title: 'Error', message: 'Absensi belum berhasil diunduh' });
      }
    },
    
    onModalClose() {
      this.currentPage = 1;
      this.trainingData = {
        training_title: '',
        batch_number: 0,
        schedule_date: '',
        total_participants: 0,
        present_count: 0,
        absent_count: 0
      };
      this.employees = [];
      this.pagination = {
        total: 0,
        page: 1,
        limit: 10,
        totalPage: 0
      };
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
.modal-body-custom {
  padding: 1.5rem;
}

.detail-item {
  margin-bottom: 0.75rem;
}

.detail-label {
  font-weight: 600;
  color: #374151;
  font-size: 0.8rem;
  margin-bottom: 0.25rem;
  display: block;
}

.detail-value {
  color: #1f2937;
  font-size: 0.85rem;
}

.badge {
  font-size: 0.7rem;
  padding: 0.3rem 0.65rem;
  font-weight: 500;
}

.badge-success {
  background-color: #10b981;
  color: white;
}

.badge-danger {
  background-color: #dc3545;
  color: white;
}

.attendance-table {
  margin-top: 1rem;
}

.attendance-table thead th {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  padding: 0.875rem 0.75rem;
  color: #6b7280;
}

.attendance-table tbody td {
  vertical-align: middle;
  padding: 0.875rem 0.75rem;
  font-size: 0.85rem;
}

.btn-sm {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
}

.btn-danger {
  background-color: #dc3545;
  border-color: #dc3545;
  color: white;
}

.btn-danger:hover {
  background-color: #bb2d3b;
  border-color: #bb2d3b;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}

.text-muted {
  color: #6b7280 !important;
}

.page-size-select {
  width: auto;
  min-width: 70px;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}
</style>
